import React from 'react';
import { ctrlOrMetaKeyPressed, shiftKeyPressed } from '@applied/utils/keyboard';
import classnames from 'classnames';

import { type Spacing, mapToSpacing } from './layout_helpers';

export interface FlexProps {
  children?: React.ReactNode;
  className?: string;
  alignment?: React.CSSProperties['alignItems'];
  justify?: React.CSSProperties['justifyContent'];
  padding?: Spacing;
  gap?: Spacing;
  direction?: React.CSSProperties['flexDirection'];
  /** Expands across the main flex axis. Equivalent to `flex: 1`. */
  grow?: boolean;
  /** Expands across the cross flex axis. Equivalent to `align-self: stretch`. */
  stretch?: boolean;
  inline?: boolean;
  wrap?: boolean;
  onClick?: React.DOMAttributes<HTMLDivElement>['onClick'];
  /**
   * Set on elements that use modifier-click (shift/cmd/ctrl) to toggle
   * selection. The browser commits text selection on mousedown — before the
   * click handler runs — so the element suppresses it when a modifier is held.
   */
  preventTextSelectionOnModifierClick?: boolean;
  minHeight?: number;
  overflow?: 'auto';
  ['data-testid']?: string;
  containerRef?: React.RefObject<HTMLDivElement>;
}

function suppressModifierTextSelection(event: React.MouseEvent<HTMLDivElement>): void {
  if (shiftKeyPressed(event) || ctrlOrMetaKeyPressed(event)) {
    event.preventDefault();
  }
}

// TODO(youngxguo): dedupe this code with the Forms/Row component, which follows a similar pattern.
export default function Flex({
  children,
  className,
  alignment,
  justify,
  padding,
  gap,
  direction = 'row',
  grow,
  stretch,
  inline,
  wrap,
  onClick,
  preventTextSelectionOnModifierClick,
  minHeight,
  overflow,
  ['data-testid']: dataTestId,
  containerRef,
}: FlexProps): JSX.Element {
  return (
    <div
      onClick={onClick}
      onMouseDown={preventTextSelectionOnModifierClick ? suppressModifierTextSelection : undefined}
      className={classnames('dragonfruit-flex', className)}
      style={{
        // For readability, all styles are defined here (i.e. no css), even
        // if they are not dynamic.
        display: inline ? 'inline-flex' : 'flex',
        flex: grow ? 1 : undefined,
        alignSelf: stretch ? 'stretch' : undefined,
        flexDirection: direction,
        alignItems: alignment,
        justifyContent: justify,
        padding: mapToSpacing(padding),
        gap: mapToSpacing(gap),
        flexWrap: wrap ? 'wrap' : undefined,
        minHeight,
        overflow,
      }}
      data-testid={dataTestId}
      ref={containerRef}
    >
      {children}
    </div>
  );
}
